import {
  PAYOFF_CALLABILITY_AMOUNT,
  PAYOFF_CALLABILITY_AMOUNT_TYPE,
  PAYOFF_INVESTOR_EXERCISE_SCHEDULE_CALENDAR,
  PAYOFF_PARTICIPATION_UPSIDE_ADJUSTMENT_DATES_CALENDAR,
  PAYOFF_PARTICIPATION_UPSIDE_ADJUSTMENT_DATES_FREQUENCY,
} from './product-capture-field-selectors.consts';
import {
  PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
  PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
  PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
  PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
  PAYOFF_PARTICIPATION_UPSIDE_DIRECTION,
  PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION,
  PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
  PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
  PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
  PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
  PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
  PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
  PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
  PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
  PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
  PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
  PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG,
  PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
  PAYOFF_PARTICIPATION_UPSIDE_TYPE,
  SUMMARY_FREQUENCY,
  SUMMARY_TENOR,
  SUMMARY_REDEMPTION_DATE,
  SUMMARY_FINAL_OBSERVATION_DATE,
  PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
  PAYOFF_PARTICIPATION_UPSIDE_STRIKE_ADJUSTMENT_RATE_BASIS,
  PAYOFF_PARTICIPATION_UPSIDE_STRIKE_ADJUSTMENT_FLOATING_REFERENCE_RATE,
  PAYOFF_PARTICIPATION_UPSIDE_STRIKE_ADJUSTMENT_FACTOR,
  PAYOFF_PARTICIPATION_UPSIDE_STRIKE_ADJUSTMENT_DAY_COUNT_BASIS,
  PAYOFF_PARTICIPATION_UPSIDE_MIN_BARRIER_ADJUSTMENT_FACTOR,
  PAYOFF_PARTICIPATION_UPSIDE_MAX_BARRIER_ADJUSTMENT_FACTOR,
  PAYOFF_INVESTOR_EXERCISE_FREQUENCY,
  PAYOFF_CALLABILITY_CALLABILITY,
  PAYOFF_CALLABILITY_CALL_EVENT_TYPE,
  PAYOFF_CALLABILITY_FREQUENCY,
  PAYOFF_CALLABILITY_DEFERRED_PERIODS,
  PAYOFF_CALLABILITY_LAST_PERIOD_CALLABLE,
} from './product-capture-field-selectors.consts';

import {
  FCFieldEffect,
  FCFieldNames,
  FCSelector,
} from '../../model/form-config.interface';
import {
  PAYOFF_PARTICIPATION_UPSIDE_BARRIER_TYPE,
  PAYOFF_PARTICIPATION_UPSIDE_BARRIER_ADJUSTMENT_FACTOR,
} from './product-capture-field-selectors.consts';

const CLEAR_BARRIER_EFFECT: FCFieldEffect = {
  formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER],
  selectValues: [null],
};

const CLEAR_SPREAD_STRIKE_EFFECT: FCFieldEffect = {
  formControlNames: [
    PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
    PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
  ],
  selectValues: [null],
};

const REDEMPTION_AND_FINAL_OBSERVATION_DATE_VISIBLE_EFFECT: FCFieldEffect = {
  formControlNames: [SUMMARY_REDEMPTION_DATE, SUMMARY_FINAL_OBSERVATION_DATE],
  hidden: false,
  readOnly: false,
};

const KNOCK_OUT_CALL_WAVE_AND_UNLIMITED_EFFECTS: FCFieldEffect[] = [
  {
    formControlNames: [
      PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
      PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
      PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
      PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
      PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
      PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
      PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
      PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
      PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
      PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
      PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
    ],
    hidden: true,
  },
  { ...CLEAR_BARRIER_EFFECT },
  { ...CLEAR_SPREAD_STRIKE_EFFECT },
  {
    formControlNames: [
      PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
      PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
      PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
    ],
    hidden: false,
    readOnly: false,
  },
  {
    formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_BARRIER_TYPE],
    hidden: true,
    readOnly: true,
    selectValues: ['FLAT'],
  },
  {
    formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY],
    selectValues: ['DAILY_CONTINUOUS'],
    hidden: false,
    readOnly: true,
  },
  {
    formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER],
    hidden: true,
    readOnly: true,
    copyFieldValueFrom: PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
  },
  {
    formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_DIRECTION],
    selectValues: ['DOWN_AND_OUT'],
    hidden: true,
    readOnly: true,
  },
  {
    formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK],
    selectValues: [true],
    hidden: true,
    readOnly: true,
  },
  {
    formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION],
    selectValues: [true],
    hidden: false,
    readOnly: true,
  },
  {
    // Need to have an explicit hide rule for this field as it can become visible once early termination is set to true for other upside types
    formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG],
    hidden: true,
  },
];

const KNOCK_OUT_PUT_WAVE_AND_UNLIMITED_EFFECTS: FCFieldEffect[] = [
  {
    formControlNames: [
      PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
      PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
      PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
      PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
      PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
      PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
      PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
      PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
      PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
      PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
      PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
    ],
    hidden: true,
  },
  { ...CLEAR_BARRIER_EFFECT },
  { ...CLEAR_SPREAD_STRIKE_EFFECT },
  {
    formControlNames: [
      PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
      PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
      PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
    ],
    hidden: false,
    readOnly: false,
  },
  {
    formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_BARRIER_TYPE],
    hidden: true,
    readOnly: true,
    selectValues: ['FLAT'],
  },
  {
    formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY],
    selectValues: ['DAILY_CONTINUOUS'],
    hidden: false,
    readOnly: true,
  },
  {
    formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER],
    hidden: true,
    readOnly: true,
    copyFieldValueFrom: PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
  },
  {
    formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_DIRECTION],
    selectValues: ['UP_AND_OUT'],
    hidden: true,
    readOnly: true,
  },
  {
    formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK],
    selectValues: [true],
    hidden: true,
    readOnly: true,
  },
  {
    formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION],
    selectValues: [true],
    hidden: false,
    readOnly: true,
  },
  {
    // Need to have an explicit hide rule for this field as it can become visible once early termination is set to true for other upside types
    formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG],
    hidden: true,
  },
];

const KNOCK_OUT_WAVE_UNLIMITED_AND_XXL_FIELDS: FCFieldNames[] = [
  PAYOFF_PARTICIPATION_UPSIDE_STRIKE_ADJUSTMENT_RATE_BASIS,
  PAYOFF_PARTICIPATION_UPSIDE_STRIKE_ADJUSTMENT_FLOATING_REFERENCE_RATE,
  PAYOFF_PARTICIPATION_UPSIDE_STRIKE_ADJUSTMENT_FACTOR,
  PAYOFF_PARTICIPATION_UPSIDE_STRIKE_ADJUSTMENT_DAY_COUNT_BASIS,
  PAYOFF_PARTICIPATION_UPSIDE_ADJUSTMENT_DATES_CALENDAR,
  PAYOFF_PARTICIPATION_UPSIDE_ADJUSTMENT_DATES_FREQUENCY,
  PAYOFF_INVESTOR_EXERCISE_FREQUENCY,
  PAYOFF_INVESTOR_EXERCISE_SCHEDULE_CALENDAR,
];

const KNOCK_OUT_WAVE_UNLIMITED_AND_XXL_EFFECTS: FCFieldEffect[] = [
  {
    formControlNames: KNOCK_OUT_WAVE_UNLIMITED_AND_XXL_FIELDS,
    hidden: false,
    readOnly: false,
  },
  {
    formControlNames: [PAYOFF_CALLABILITY_CALL_EVENT_TYPE],
    selectValues: ['ISSUER_CALL'],
    hidden: false,
    readOnly: false,
  },
  {
    formControlNames: [PAYOFF_CALLABILITY_FREQUENCY],
    selectValues: ['1B'],
    hidden: false,
    readOnly: false,
  },
];

const HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS: FCFieldEffect = {
  formControlNames: [
    ...KNOCK_OUT_WAVE_UNLIMITED_AND_XXL_FIELDS,
    PAYOFF_PARTICIPATION_UPSIDE_BARRIER_ADJUSTMENT_FACTOR,
    PAYOFF_PARTICIPATION_UPSIDE_MAX_BARRIER_ADJUSTMENT_FACTOR,
    PAYOFF_PARTICIPATION_UPSIDE_MIN_BARRIER_ADJUSTMENT_FACTOR,
  ],
  hidden: true,
  readOnly: false,
};

const FREQUENCY_AND_TENOR_OPEN_ENDED_EFFECTS: FCFieldEffect[] = [
  {
    formControlNames: [SUMMARY_FREQUENCY],
    hidden: true,
    readOnly: true,
    selectValues: ['OPEN_ENDED'],
  },
  {
    formControlNames: [SUMMARY_TENOR],
    selectValues: ['Open Ended'],
    hidden: false,
    readOnly: true,
  },
  {
    formControlNames: [SUMMARY_REDEMPTION_DATE],
    hidden: true,
    readOnly: false,
  },
  {
    formControlNames: [SUMMARY_FINAL_OBSERVATION_DATE],
    hidden: true,
    readOnly: false,
  },
];

const FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS: FCFieldEffect[] = [
  {
    formControlNames: [SUMMARY_FREQUENCY],
    selectValues: ['AT_MATURITY'],
    hidden: false,
    readOnly: true,
  },
  {
    formControlNames: [SUMMARY_TENOR],
    selectValues: [null],
    hidden: false,
    readOnly: false,
  },
  { ...REDEMPTION_AND_FINAL_OBSERVATION_DATE_VISIBLE_EFFECT },
];

export const UPSIDE_TYPE: FCSelector = {
  formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_TYPE],
  options: [
    {
      id: 'NONE',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
          ],
          hidden: true,
        },
        {
          formControlNames: [SUMMARY_FREQUENCY, SUMMARY_TENOR],
          selectValues: [null],
          hidden: false,
          readOnly: false,
        },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG,
          ],
          selectValues: [null],
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        { ...REDEMPTION_AND_FINAL_OBSERVATION_DATE_VISIBLE_EFFECT },
      ],
    },
    {
      id: 'CALL',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
          ],
          hidden: true,
        },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
          ],
          hidden: false,
        },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG,
          ],
          selectValues: [null],
        },
        {
          formControlNames: [SUMMARY_FREQUENCY, SUMMARY_TENOR],
          selectValues: [null],
          hidden: false,
          readOnly: false,
        },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        { ...REDEMPTION_AND_FINAL_OBSERVATION_DATE_VISIBLE_EFFECT },
        { ...CLEAR_BARRIER_EFFECT },
      ],
    },
    {
      id: 'KNOCK_OUT_CALL',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
          ],
          hidden: true,
        },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
          ],
          hidden: false,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_BARRIER_TYPE],
          hidden: true,
          readOnly: true,
          selectValues: ['FLAT'],
        },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION,
          ],
          selectValues: [false],
          hidden: false,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY],
          selectValues: ['PERIOD_END'],
          hidden: false,
        },
        {
          formControlNames: [SUMMARY_FREQUENCY, SUMMARY_TENOR],
          selectValues: [null],
          hidden: false,
          readOnly: false,
        },
        { ...REDEMPTION_AND_FINAL_OBSERVATION_DATE_VISIBLE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
      ],
    },
    {
      id: 'TWIN_WIN',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
          ],
          hidden: true,
        },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
          ],
          hidden: false,
        },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG,
          ],
          selectValues: [null],
        },
        {
          formControlNames: [SUMMARY_FREQUENCY, SUMMARY_TENOR],
          selectValues: [null],
          hidden: false,
          readOnly: false,
        },
        { ...REDEMPTION_AND_FINAL_OBSERVATION_DATE_VISIBLE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        { ...CLEAR_BARRIER_EFFECT },
      ],
    },
    {
      id: 'KNOCK_OUT_TWIN_WIN',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
          ],
          hidden: true,
        },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
          ],
          hidden: false,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_BARRIER_TYPE],
          hidden: true,
          readOnly: true,
          selectValues: ['FLAT'],
        },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG,
          ],
          selectValues: [null],
        },
        {
          formControlNames: [SUMMARY_FREQUENCY, SUMMARY_TENOR],
          selectValues: [null],
          hidden: false,
          readOnly: false,
        },
        { ...REDEMPTION_AND_FINAL_OBSERVATION_DATE_VISIBLE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
      ],
    },
    {
      id: 'DIGITAL',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
          ],
          hidden: true,
        },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
          ],
          hidden: false,
        },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG,
          ],
          selectValues: [null],
        },
        {
          formControlNames: [SUMMARY_FREQUENCY, SUMMARY_TENOR],
          selectValues: [null],
          hidden: false,
          readOnly: false,
        },
        { ...REDEMPTION_AND_FINAL_OBSERVATION_DATE_VISIBLE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
      ],
    },
    {
      id: 'CALL_WARRANT',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
            PAYOFF_CALLABILITY_CALLABILITY,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
          ],
          hidden: false,
          readOnly: false,
        },
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
      ],
    },
    {
      id: 'PUT_WARRANT',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
            PAYOFF_CALLABILITY_CALLABILITY,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
          ],
          hidden: false,
          readOnly: false,
        },
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
      ],
    },
    {
      id: 'CALL_DISCOUNT',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
            PAYOFF_CALLABILITY_CALLABILITY,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
          ],
          hidden: false,
          readOnly: false,
        },
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
      ],
    },
    {
      id: 'PUT_DISCOUNT',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
            PAYOFF_CALLABILITY_CALLABILITY,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
          ],
          hidden: false,
          readOnly: false,
        },
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
      ],
    },
    {
      id: 'KNOCK_OUT_CALL_WAVE',
      effects: [
        ...KNOCK_OUT_CALL_WAVE_AND_UNLIMITED_EFFECTS,
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
        {
          formControlNames: [PAYOFF_CALLABILITY_CALLABILITY],
          hidden: true,
          readOnly: false,
        },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
      ],
    },
    {
      id: 'KNOCK_OUT_PUT_WAVE',
      effects: [
        ...KNOCK_OUT_PUT_WAVE_AND_UNLIMITED_EFFECTS,
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
        {
          formControlNames: [PAYOFF_CALLABILITY_CALLABILITY],
          hidden: true,
          readOnly: false,
        },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
      ],
    },
    {
      id: 'KNOCK_OUT_CALL_WAVE_UNLIMITED',
      effects: [
        ...KNOCK_OUT_CALL_WAVE_AND_UNLIMITED_EFFECTS,
        ...KNOCK_OUT_WAVE_UNLIMITED_AND_XXL_EFFECTS,
        ...FREQUENCY_AND_TENOR_OPEN_ENDED_EFFECTS,
        {
          formControlNames: [
            PAYOFF_CALLABILITY_CALL_EVENT_TYPE,
            PAYOFF_CALLABILITY_FREQUENCY,
            PAYOFF_CALLABILITY_DEFERRED_PERIODS,
            PAYOFF_CALLABILITY_LAST_PERIOD_CALLABLE,
          ],
          hidden: false,
          readOnly: false,
        },
        {
          formControlNames: [SUMMARY_REDEMPTION_DATE],
          hidden: true,
          readOnly: false,
        },
      ],
    },
    {
      id: 'KNOCK_OUT_PUT_WAVE_UNLIMITED',
      effects: [
        ...KNOCK_OUT_PUT_WAVE_AND_UNLIMITED_EFFECTS,
        ...KNOCK_OUT_WAVE_UNLIMITED_AND_XXL_EFFECTS,
        ...FREQUENCY_AND_TENOR_OPEN_ENDED_EFFECTS,
        {
          formControlNames: [
            PAYOFF_CALLABILITY_CALL_EVENT_TYPE,
            PAYOFF_CALLABILITY_FREQUENCY,
            PAYOFF_CALLABILITY_DEFERRED_PERIODS,
            PAYOFF_CALLABILITY_LAST_PERIOD_CALLABLE,
          ],
          hidden: false,
          readOnly: false,
        },
        {
          formControlNames: [SUMMARY_REDEMPTION_DATE],
          hidden: true,
          readOnly: false,
        },
      ],
    },
    {
      id: 'KNOCK_OUT_CALL_WAVE_XXL',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_BARRIER_TYPE],
          hidden: true,
          readOnly: true,
          selectValues: ['FLAT'],
        },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
            PAYOFF_PARTICIPATION_UPSIDE_BARRIER_ADJUSTMENT_FACTOR,
            PAYOFF_PARTICIPATION_UPSIDE_MAX_BARRIER_ADJUSTMENT_FACTOR,
            PAYOFF_PARTICIPATION_UPSIDE_MIN_BARRIER_ADJUSTMENT_FACTOR,
            PAYOFF_CALLABILITY_CALL_EVENT_TYPE,
            PAYOFF_CALLABILITY_FREQUENCY,
            PAYOFF_CALLABILITY_DEFERRED_PERIODS,
            PAYOFF_CALLABILITY_LAST_PERIOD_CALLABLE,
          ],
          hidden: false,
          readOnly: false,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY],
          selectValues: ['DAILY_CONTINUOUS'],
          hidden: false,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_DIRECTION],
          selectValues: ['DOWN_AND_OUT'],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK],
          selectValues: [true],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION],
          selectValues: [true],
          hidden: false,
          readOnly: true,
        },
        {
          // Need to have an explicit hide rule for this field as it can become visible once early termination is set to true for other upside types
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG],
          hidden: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER],
          hidden: false,
          readOnly: false,
        },
        ...KNOCK_OUT_WAVE_UNLIMITED_AND_XXL_EFFECTS,
        ...FREQUENCY_AND_TENOR_OPEN_ENDED_EFFECTS,
        {
          formControlNames: [SUMMARY_REDEMPTION_DATE],
          hidden: true,
          readOnly: false,
        },
      ],
    },
    {
      id: 'KNOCK_OUT_PUT_WAVE_XXL',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
            PAYOFF_PARTICIPATION_UPSIDE_BARRIER_ADJUSTMENT_FACTOR,
            PAYOFF_PARTICIPATION_UPSIDE_MAX_BARRIER_ADJUSTMENT_FACTOR,
            PAYOFF_PARTICIPATION_UPSIDE_MIN_BARRIER_ADJUSTMENT_FACTOR,
            PAYOFF_CALLABILITY_CALL_EVENT_TYPE,
            PAYOFF_CALLABILITY_FREQUENCY,
            PAYOFF_CALLABILITY_DEFERRED_PERIODS,
            PAYOFF_CALLABILITY_LAST_PERIOD_CALLABLE,
          ],
          hidden: false,
          readOnly: false,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_BARRIER_TYPE],
          hidden: true,
          readOnly: true,
          selectValues: ['FLAT'],
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY],
          selectValues: ['DAILY_CONTINUOUS'],
          hidden: false,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_DIRECTION],
          selectValues: ['UP_AND_OUT'],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK],
          selectValues: [true],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION],
          selectValues: [true],
          hidden: false,
          readOnly: true,
        },
        {
          // Need to have an explicit hide rule for this field as it can become visible once early termination is set to true for other upside types
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG],
          hidden: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER],
          hidden: false,
          readOnly: false,
        },
        ...KNOCK_OUT_WAVE_UNLIMITED_AND_XXL_EFFECTS,
        ...FREQUENCY_AND_TENOR_OPEN_ENDED_EFFECTS,
        {
          formControlNames: [SUMMARY_REDEMPTION_DATE],
          hidden: true,
          readOnly: false,
        },
      ],
    },
    {
      id: 'KNOCK_IN_DIGITAL_CALL',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_CALLABILITY_CALLABILITY,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
          ],
          hidden: false,
          readOnly: false,
        },
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_BARRIER_TYPE],
          hidden: true,
          readOnly: true,
          selectValues: ['FLAT'],
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY],
          selectValues: ['DAILY_CONTINUOUS'],
          hidden: false,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_DIRECTION],
          selectValues: ['UP_AND_IN'],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK],
          selectValues: [true],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION],
          selectValues: [true],
          hidden: false,
          readOnly: true,
        },
        {
          // Need to have an explicit hide rule for this field as it can become visible once early termination is set to true for other upside types
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG],
          hidden: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER],
          hidden: false,
          readOnly: false,
        },
      ],
    },
    {
      id: 'KNOCK_IN_DIGITAL_PUT',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_CALLABILITY_CALLABILITY,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            SUMMARY_FREQUENCY,
          ],
          hidden: false,
          readOnly: false,
        },
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_BARRIER_TYPE],
          hidden: true,
          readOnly: true,
          selectValues: ['FLAT'],
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY],
          selectValues: ['DAILY_CONTINUOUS'],
          hidden: false,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_DIRECTION],
          selectValues: ['DOWN_AND_IN'],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK],
          selectValues: [true],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION],
          selectValues: [true],
          hidden: false,
          readOnly: true,
        },
        {
          // Need to have an explicit hide rule for this field as it can become visible once early termination is set to true for other upside types
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG],
          hidden: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER],
          hidden: false,
          readOnly: false,
        },
      ],
    },
    {
      id: 'KNOCK_OUT_CALL_BARRIER',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_CALLABILITY_CALLABILITY,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
          ],
          hidden: false,
          readOnly: false,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_BARRIER_TYPE],
          hidden: true,
          readOnly: true,
          selectValues: ['FLAT'],
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY],
          selectValues: ['DAILY_CONTINUOUS'],
          hidden: false,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_DIRECTION],
          selectValues: ['UP_AND_OUT'],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK],
          selectValues: [true],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION],
          selectValues: [true],
          hidden: false,
          readOnly: true,
        },
        {
          // Need to have an explicit hide rule for this field as it can become visible once early termination is set to true for other upside types
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG],
          hidden: true,
        },
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
      ],
    },
    {
      id: 'KNOCK_OUT_PUT_BARRIER',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
            PAYOFF_CALLABILITY_CALLABILITY,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
          ],
          hidden: false,
          readOnly: false,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_BARRIER_TYPE],
          hidden: true,
          readOnly: true,
          selectValues: ['FLAT'],
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY],
          selectValues: ['DAILY_CONTINUOUS'],
          hidden: false,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_DIRECTION],
          selectValues: ['DOWN_AND_OUT'],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK],
          selectValues: [true],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION],
          selectValues: [true],
          hidden: false,
          readOnly: true,
        },
        {
          // Need to have an explicit hide rule for this field as it can become visible once early termination is set to true for other upside types
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG],
          hidden: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER],
          hidden: false,
          readOnly: false,
        },
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
      ],
    },
    {
      id: 'KNOCK_OUT_DIGITAL_CALL',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_CALLABILITY_CALLABILITY,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
          ],
          hidden: false,
          readOnly: false,
        },
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_BARRIER_TYPE],
          hidden: true,
          readOnly: true,
          selectValues: ['FLAT'],
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY],
          selectValues: ['DAILY_CONTINUOUS'],
          hidden: false,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_DIRECTION],
          selectValues: ['DOWN_AND_OUT'],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK],
          selectValues: [true],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION],
          selectValues: [true],
          hidden: false,
          readOnly: true,
        },
        {
          // Need to have an explicit hide rule for this field as it can become visible once early termination is set to true for other upside types
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG],
          hidden: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER],
          hidden: false,
          readOnly: false,
        },
      ],
    },
    {
      id: 'KNOCK_OUT_DIGITAL_PUT',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_CALLABILITY_CALLABILITY,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
          ],
          hidden: false,
          readOnly: false,
        },
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_BARRIER_TYPE],
          hidden: true,
          readOnly: true,
          selectValues: ['FLAT'],
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY],
          selectValues: ['DAILY_CONTINUOUS'],
          hidden: false,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_DIRECTION],
          selectValues: ['UP_AND_OUT'],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK],
          selectValues: [true],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION],
          selectValues: [true],
          hidden: false,
          readOnly: true,
        },
        {
          // Need to have an explicit hide rule for this field as it can become visible once early termination is set to true for other upside types
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG],
          hidden: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER],
          hidden: false,
          readOnly: false,
        },
      ],
    },
    {
      id: 'KNOCK_OUT_FIXED_KNOCK_IN_CALL',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_CALLABILITY_CALLABILITY,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
          ],
          hidden: false,
          readOnly: false,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_BARRIER_TYPE],
          hidden: true,
          readOnly: true,
          selectValues: ['FLAT'],
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY],
          selectValues: ['DAILY_CONTINUOUS'],
          hidden: false,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_DIRECTION],
          selectValues: ['DOWN_AND_OUT'],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK],
          selectValues: [true],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION],
          selectValues: [false],
          hidden: false,
          readOnly: true,
        },
        {
          // Need to have an explicit hide rule for this field as it can become visible once early termination is set to true for other upside types
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG],
          hidden: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER],
          hidden: false,
          readOnly: false,
        },
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
      ],
    },
    {
      id: 'KNOCK_OUT_FIXED_KNOCK_IN_PUT',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_CALLABILITY_CALLABILITY,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
          ],
          hidden: false,
          readOnly: false,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_BARRIER_TYPE],
          hidden: true,
          readOnly: true,
          selectValues: ['FLAT'],
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY],
          selectValues: ['DAILY_CONTINUOUS'],
          hidden: false,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_DIRECTION],
          selectValues: ['DOWN_AND_OUT'],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK],
          selectValues: [true],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION],
          selectValues: [false],
          hidden: false,
          readOnly: true,
        },
        {
          // Need to have an explicit hide rule for this field as it can become visible once early termination is set to true for other upside types
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG],
          hidden: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER],
          hidden: false,
          readOnly: false,
        },
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
      ],
    },
    {
      id: 'KNOCK_OUT_DIGITAL_CALL_WINDOW',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_CALLABILITY_CALLABILITY,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
          ],
          hidden: false,
          readOnly: false,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_BARRIER_TYPE],
          hidden: true,
          readOnly: true,
          selectValues: ['FLAT'],
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY],
          selectValues: ['DAILY_CONTINUOUS'],
          hidden: false,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_DIRECTION],
          selectValues: ['DOWN_AND_OUT'],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK],
          selectValues: [true],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION],
          selectValues: [true],
          hidden: false,
          readOnly: true,
        },
        {
          // Need to have an explicit hide rule for this field as it can become visible once early termination is set to true for other upside types
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG],
          hidden: true,
        },
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
      ],
    },
    {
      id: 'KNOCK_OUT_DIGITAL_PUT_WINDOW',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_CALLABILITY_CALLABILITY,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
          ],
          hidden: false,
          readOnly: false,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_BARRIER_TYPE],
          hidden: true,
          readOnly: true,
          selectValues: ['FLAT'],
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY],
          selectValues: ['DAILY_CONTINUOUS'],
          hidden: false,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_DIRECTION],
          selectValues: ['UP_AND_OUT'],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK],
          selectValues: [true],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION],
          selectValues: [true],
          hidden: false,
          readOnly: true,
        },
        {
          // Need to have an explicit hide rule for this field as it can become visible once early termination is set to true for other upside types
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG],
          hidden: true,
        },
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
      ],
    },
    {
      id: 'KNOCK_OUT_DIGITAL_CORRIDOR',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_CALLABILITY_CALLABILITY,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
          ],
          hidden: false,
          readOnly: false,
        },
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY],
          selectValues: ['DAILY_CONTINUOUS'],
          hidden: false,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_BARRIER_TYPE],
          selectValues: ['FLAT'],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_DIRECTION],
          selectValues: [null],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK],
          selectValues: [true],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION],
          selectValues: [true],
          hidden: false,
          readOnly: true,
        },
        {
          // Need to have an explicit hide rule for this field as it can become visible once early termination is set to true for other upside types
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG],
          hidden: true,
        },
      ],
    },
    {
      id: 'KNOCK_OUT_FIXED_KNOCK_IN_STRADDLE',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_CALLABILITY_CALLABILITY,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
          ],
          hidden: false,
          readOnly: false,
        },
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY],
          selectValues: ['DAILY_CONTINUOUS'],
          hidden: false,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_BARRIER_TYPE],
          selectValues: ['FLAT'],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_DIRECTION],
          selectValues: [null],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK],
          selectValues: [true],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION],
          selectValues: [false],
          hidden: false,
          readOnly: true,
        },
        {
          // Need to have an explicit hide rule for this field as it can become visible once early termination is set to true for other upside types
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG],
          hidden: true,
        },
      ],
    },
    {
      id: 'KNOCK_OUT_DIGITAL_CORRIDOR_WINDOW',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_CALLABILITY_CALLABILITY,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
          ],
          hidden: false,
          readOnly: false,
        },
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY],
          selectValues: ['DAILY_CONTINUOUS'],
          hidden: false,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_BARRIER_TYPE],
          selectValues: ['FLAT'],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_DIRECTION],
          selectValues: [null],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK],
          selectValues: [true],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION],
          selectValues: [true],
          hidden: false,
          readOnly: true,
        },
        {
          // Need to have an explicit hide rule for this field as it can become visible once early termination is set to true for other upside types
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG],
          hidden: true,
        },
      ],
    },
    {
      id: 'DIGITAL_CALL',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
            PAYOFF_CALLABILITY_CALLABILITY,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
          ],
          hidden: false,
          readOnly: false,
        },
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_DIRECTION],
          selectValues: [null],
          hidden: true,
          readOnly: true,
        },
      ],
    },
    {
      id: 'DIGITAL_PUT',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
            PAYOFF_CALLABILITY_CALLABILITY,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
          ],
          hidden: false,
          readOnly: false,
        },
      ],
    },
    {
      id: 'KNOCK_OUT_CALL_BARRIER_PLUS',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
            PAYOFF_CALLABILITY_CALLABILITY,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
          ],
          hidden: false,
          readOnly: false,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY],
          selectValues: ['PERIOD_END'],
          hidden: false,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK],
          selectValues: [true],
          hidden: true,
          readOnly: true,
        },
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_DIRECTION],
          selectValues: ['UP_AND_OUT'],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_BARRIER_TYPE],
          hidden: true,
          readOnly: true,
          selectValues: ['FLAT'],
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION],
          selectValues: [false],
          hidden: true,
          readOnly: true,
        },
      ],
    },
    {
      id: 'KNOCK_OUT_PUT_BARRIER_PLUS',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG,
            PAYOFF_PARTICIPATION_UPSIDE_LOWER_BARRIER_LEVEL,
            PAYOFF_PARTICIPATION_UPSIDE_UPPER_BARRIER_LEVEL,
            PAYOFF_CALLABILITY_CALLABILITY,
          ],
          hidden: true,
        },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_MINIMUM_RETURN,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
          ],
          hidden: false,
          readOnly: false,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY],
          selectValues: ['PERIOD_END'],
          hidden: false,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK],
          selectValues: [true],
          hidden: true,
          readOnly: true,
        },
        ...FREQUENCY_AND_TENOR_AT_MATURITY_EFFECTS,
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_DIRECTION],
          selectValues: ['DOWN_AND_OUT'],
          hidden: true,
          readOnly: true,
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_BARRIER_TYPE],
          hidden: true,
          readOnly: true,
          selectValues: ['FLAT'],
        },
        {
          formControlNames: [PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION],
          selectValues: [false],
          hidden: true,
          readOnly: true,
        },
      ],
    },
    {
      id: '',
      effects: [
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_CALL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_CALL_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_PARTICIPATION,
            PAYOFF_PARTICIPATION_UPSIDE_PUT_SPREAD_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_DIGITAL_STRIKE,
            PAYOFF_PARTICIPATION_UPSIDE_KNOCK_OUT_BARRIER,
            PAYOFF_PARTICIPATION_UPSIDE_OBS_FREQUENCY,
            PAYOFF_PARTICIPATION_UPSIDE_TOUCH_BREAK,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_AMOUNT,
            PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG,
          ],
          hidden: false,
        },
        {
          formControlNames: [
            PAYOFF_PARTICIPATION_UPSIDE_EARLY_TERMINATION,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_DATE,
            PAYOFF_PARTICIPATION_UPSIDE_REBATE_SETTLEMENT_LAG,
          ],
          selectValues: [null],
        },
        {
          formControlNames: [SUMMARY_FREQUENCY, SUMMARY_TENOR],
          selectValues: [null],
          hidden: false,
          readOnly: false,
        },
        { ...REDEMPTION_AND_FINAL_OBSERVATION_DATE_VISIBLE_EFFECT },
        { ...CLEAR_BARRIER_EFFECT },
        { ...CLEAR_SPREAD_STRIKE_EFFECT },
        { ...HIDE_WAVE_UNLIMITED_AND_XXL_EFFECTS },
      ],
    },
  ],
};
